import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { Brand } from "@/components/Brand";
import { DisclosureFooter } from "@/components/FundingDisclosure";
import { useTour } from "@/components/tour/TourProvider";
import { TERMS, PRIVACY, LEGAL_UI, DISCLOSURE, CONTRACTOR_TEXT } from "@/lib/legal";

export function LangToggle() {
  const { lang, setLang } = useTour();
  return (
    <div className="flex gap-1 text-xs" data-testid="legal-lang-toggle">
      {[["en", "English"], ["es", "Español"]].map(([k, l]) => (
        <button key={k} onClick={() => setLang(k)} data-testid={`legal-lang-${k}`} className={`px-2 sm:px-2.5 py-1 rounded-full border transition-colors ${lang === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-300 text-slate-600 hover:border-slate-500"}`}><span className="xl:hidden">{k.toUpperCase()}</span><span className="hidden xl:inline">{l}</span></button>
      ))}
    </div>
  );
}

function DisclaimerBox({ L }) {
  return (
    <div className="mt-10 rounded-lg border-2 border-amber-400 bg-amber-50 p-6 flex gap-4" role="note" data-testid="terms-funding-disclaimer">
      <AlertTriangle className="h-6 w-6 text-amber-600 shrink-0 mt-0.5" />
      <div><div className="font-display text-lg font-extrabold text-slate-900 uppercase tracking-wide">{DISCLOSURE[L].label}</div><p className="mt-2 text-slate-800 leading-relaxed font-medium">{DISCLOSURE[L].text}</p></div>
    </div>
  );
}

function Contractor({ L }) {
  const [t, setT] = useState(null);
  const C = CONTRACTOR_TEXT[L];
  useEffect(() => { api.get("/partners/terms").then((r) => setT(r.data)).catch(() => setT({ types: [], agreement: [], agreement_es: [] })); }, []);
  if (!t) return <Loader2 className="h-5 w-5 mt-10 animate-spin text-emerald-600" />;
  const clauses = L === "es" ? t.agreement_es : t.agreement;
  return (
    <div className="mt-10 space-y-10" data-testid="contractor-agreement-body">
      <p className="text-slate-700 leading-relaxed text-lg">{C.intro}</p>
      <p className="text-slate-600 leading-relaxed">{C.how}</p>
      <div>
        <h2 className="font-display text-lg font-bold text-slate-900">{C.feesTitle}</h2>
        <div className="mt-3 grid sm:grid-cols-3 gap-3">{t.types.map((x) => <div key={x.key} className="border border-slate-200 rounded-lg p-4" data-testid={`contractor-fee-${x.key}`}><div className="text-sm text-slate-600">{L === "es" ? x.label_es : x.label}</div><div className="font-display text-3xl font-extrabold text-slate-900 mt-1">${x.rate}</div><div className="text-xs text-slate-500">{C.perJob}</div></div>)}</div>
      </div>
      <div>
        <h2 className="font-display text-lg font-bold text-slate-900">{C.termsTitle}</h2>
        {L === "es" && <p className="text-xs italic text-slate-500 mt-1">{t.es_governs}</p>}
        <ol className="mt-4 space-y-4" data-testid="contractor-clauses">{clauses.map((c, i) => <li key={i} className="grid grid-cols-12 gap-3"><span className="col-span-1 font-mono text-xs text-emerald-600 mt-1">{String(i + 1).padStart(2, "0")}</span><p className="col-span-11 text-slate-600 leading-relaxed">{c}</p></li>)}</ol>
      </div>
      <p className="text-slate-600">{C.tos}</p>
      <Link to="/partners" className="inline-block" data-testid="contractor-apply-link"><span className="inline-flex items-center h-11 px-6 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors">{C.apply}</span></Link>
    </div>
  );
}

const DESC = {
  en: { terms: "GrantVerify Terms of Service and Funding Disclosure: document preparation software and marketplace review services by Peer Verify LLC. Funding is not guaranteed.",
        privacy: "How GrantVerify by Peer Verify LLC collects, uses and protects your business and account information.",
        contractor: "The Marketplace Independent Contractor Agreement for certified grant editors, CPAs and business attorneys providing flat-fee services through Peer Verify LLC." },
  es: { terms: "Términos de servicio y Aviso sobre financiamiento de GrantVerify, de Peer Verify LLC. No se garantiza el financiamiento.",
        privacy: "Cómo GrantVerify de Peer Verify LLC recopila, usa y protege su información.",
        contractor: "Acuerdo de contratista independiente para editores de subvenciones, contadores y abogados que prestan servicios con tarifa fija a través de Peer Verify LLC." },
};

const PAGE = { "/terms": "terms", "/privacy": "privacy", "/contractor-agreement": "contractor" };

export default function Legal() {
  const kind = PAGE[useLocation().pathname] || "terms";
  const isTerms = kind === "terms";
  const { lang } = useTour();
  const L = lang === "es" ? "es" : "en";
  const sections = kind === "contractor" ? [] : (isTerms ? TERMS : PRIVACY)[L];
  const ui = LEGAL_UI[L];
  const title = { terms: DISCLOSURE[L].termsTitle, privacy: DISCLOSURE[L].privacy, contractor: DISCLOSURE[L].contractorTitle }[kind];
  usePageMeta({ title: `${title} | GrantVerify`, description: DESC[L][kind], path: `/${kind === "contractor" ? "contractor-agreement" : kind}`, image: L === "es" ? "og-image-es.png" : undefined });
  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`${kind}-page`} data-no-translate>
      <header className="border-b border-slate-200"><div className="max-w-4xl mx-auto px-6 py-5 flex justify-between items-center gap-4"><Brand /><div className="flex items-center gap-3 sm:gap-5"><LangToggle /><Link to="/" className="text-sm text-slate-500 hover:text-slate-900 flex items-center gap-1" data-testid="legal-home-link"><ArrowLeft className="h-4 w-4" /><span className="hidden sm:inline">{ui.home}</span></Link></div></div></header>
      <main className="max-w-4xl mx-auto px-6 py-16 flex-1 w-full fade-up" lang={L}>
        <div className="eyebrow mb-3">{ui.eyebrow}</div>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900" data-testid="legal-title">{title}</h1>
        <p className="text-sm text-slate-500 mt-3">{ui.updated}</p>
        {ui.note && <p className="text-xs text-slate-500 mt-2 italic" data-testid="legal-translation-note">{ui.note}</p>}
        {isTerms && <DisclaimerBox L={L} />}
        {kind === "contractor" && <Contractor L={L} />}
        <div className="mt-12 space-y-10">
          {sections.map(([h, b], i) => (
            <section key={i} className="grid md:grid-cols-12 gap-3" data-testid={`legal-section-${i}`}>
              <h2 className="md:col-span-4 font-display text-lg font-bold text-slate-900"><span className="font-mono text-xs text-emerald-600 mr-2">{String(i + 1).padStart(2, "0")}</span>{h}</h2>
              <p className="md:col-span-8 text-slate-600 leading-relaxed">{b}</p>
            </section>
          ))}
        </div>
      </main>
      <DisclosureFooter />
    </div>
  );
}
